// ThemeDialog.tsx
// テーマ編集ダイアログ

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { ThemeType } from '../data/ThemeType';
import ThemeSettingPanel from './ThemeSettingPanel';
import ThemePreview from './ThemePreview';
import { createTheme, updateTheme } from '../../../service/ThemeTypeService';

interface ThemeDialogProps {
  open: boolean;
  theme: ThemeType | null;
  onSave: (theme: ThemeType) => void;
  onCancel: () => void;
  onExited?: () => void; // ダイアログ閉じるアニメーション完了後のコールバック
}

// バリデーションエラーのインターフェース
interface ValidationErrors {
  themeName?: string;
}

/**
 * テーマ編集・新規追加ダイアログコンポーネント
 * - テーマ名・アイコン・カラー設定と即時プレビューを提供する
 * - 保存時に新規作成または更新APIを呼び出す
 * - props: open (ダイアログ表示状態), theme (編集対象テーマ、nullの場合は新規作成),
 *          onSave (保存コールバック), onCancel (キャンセルコールバック)
 */
export default function ThemeDialog({
  open,
  theme,
  onSave,
  onCancel,
  onExited,
}: ThemeDialogProps) {
  // フォームデータ
  const [formData, setFormData] = useState<ThemeType>({
    id: '',
    themeName: '',
    iconName: 'AssignmentTwoToneIcon',
    iconColor: '#1976d2',
    iconBgColor: '#e3f2fd',
    iconBdColor: '#90caf9',
    buttonColor: '#e3f2fd',
    fontColor: '#1565c0',
    buttonBdColor: '#90caf9',
    themeDescription: '',
  });

  // バリデーションエラー
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // 既存データの読み込み
  useEffect(() => {
    // ダイアログが開いている時のみデータを更新
    if (open) {
      if (theme) {
        setFormData(theme);
      } else {
        // 新規作成の場合、初期値を設定
        setFormData({
          id: '',
          themeName: '',
          iconName: 'AssignmentTwoToneIcon',
          iconColor: '#1976d2',
          iconBgColor: '#e3f2fd',
          iconBdColor: '#90caf9',
          buttonColor: '#e3f2fd',
          fontColor: '#1565c0',
          buttonBdColor: '#90caf9',
          themeDescription: '',
        });
      }
      // ダイアログを開く際にエラーをクリア
      setValidationErrors({});
    }
  }, [theme, open]);

  // テーマ詳細設定変更
  const handleThemeChange = (updatedTheme: Omit<ThemeType, 'id'>) => {
    setFormData({ ...formData, ...updatedTheme });
    // テーマ名が変更された場合、エラーをクリア
    if (updatedTheme.themeName && validationErrors.themeName && updatedTheme.themeName.trim()) {
      setValidationErrors({ ...validationErrors, themeName: undefined });
    }
  };

  // 保存処理
  const handleSave = async () => {
    // バリデーション
    const errors: ValidationErrors = {};

    if (!formData.themeName.trim()) {
      errors.themeName = '※テーマ名を入力してください';
    }

    // エラーがある場合は保存しない
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    let success = false;

    if (theme) {
      // 既存データ更新
      success = await updateTheme(formData.id, {
        themeName: formData.themeName,
        iconName: formData.iconName,
        iconColor: formData.iconColor,
        iconBgColor: formData.iconBgColor,
        iconBdColor: formData.iconBdColor,
        buttonColor: formData.buttonColor,
        fontColor: formData.fontColor,
        buttonBdColor: formData.buttonBdColor,
        themeDescription: formData.themeDescription,
      });

      if (success) {
        onSave(formData);
      } else {
        alert('更新に失敗しました');
        return;
      }
    } else {
      // 新規追加
      success = await createTheme({
        themeName: formData.themeName,
        iconName: formData.iconName,
        iconColor: formData.iconColor,
        iconBgColor: formData.iconBgColor,
        iconBdColor: formData.iconBdColor,
        buttonColor: formData.buttonColor,
        fontColor: formData.fontColor,
        buttonBdColor: formData.buttonBdColor,
        themeDescription: formData.themeDescription,
      });

      if (success) {
        onSave(formData);
      } else {
        alert('作成に失敗しました');
        return;
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      TransitionProps={{
        onExited: onExited,
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body1" fontWeight="bold" sx={{ color: 'GrayText' }}>
            {theme ? 'テーマ編集' : 'テーマ新規追加'}
          </Typography>
          <EditIcon sx={{ fontSize: 20, color: 'GrayText' }} />
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* 左側: テーマ設定 */}
          <Grid size={{ xs: 12, md: 7 }}>
            <ThemeSettingPanel
              theme={formData}
              onChange={handleThemeChange}
              validationErrors={validationErrors}
            />
          </Grid>

          {/* 右側: プレビュー */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ position: 'sticky', top: 20 }}>
              <ThemePreview
                typeName={formData.themeName || 'テーマ名未設定'}
                theme={formData}
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onCancel} color="inherit">
          キャンセル
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          保存
        </Button>
      </DialogActions>
    </Dialog>
  );
}
